import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees, requestLeave } from '../../redux/employer-employeeSlice/EmployerEmployeeSlice';
import { BiX, BiDownload } from 'react-icons/bi';
import { toast } from 'sonner';
import EmployeeAttendance from './EmployeeAttendance';

const EmployerEmployeeJobSeeker = () => {
  const dispatch = useDispatch();
  const { employees, status, error } = useSelector((state) => state.employerEmployee || {});
  const { currentUser } = useSelector((state) => state.user || {});
  const [employee, setEmployee] = useState(null);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [isLeaveSubmitting, setIsLeaveSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!currentUser?._id) {
      console.warn('No currentUser._id found');
      return;
    }

    console.log('Fetching employee for jobSeekerId:', currentUser._id);
    console.log('Current Redux state:', { employees, status, error });

    dispatch(fetchEmployees({ id: currentUser._id, userType: 'jobSeeker' })) // Pass userType as 'jobSeeker'
      .unwrap()
      .then((data) => {
        console.log('Fetched data from thunk:', data);
        // Handle array or single object response
        const employeeData = Array.isArray(data) ? data.find((emp) => emp._id === currentUser._id) : data;
        if (employeeData && employeeData._id) {
          setEmployee(employeeData);
          console.log('Updated local employee state:', employeeData);
        } else {
          console.warn('No matching employee found in data:', data);
          setEmployee(null);
        }
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setNotification({ message: 'Failed to load employee data', type: 'error' });
      });
  }, [currentUser?._id, dispatch]);

  const handleRequestLeave = async (e) => {
    e.preventDefault();
    if (!employee) {
      setNotification({ message: 'No employee data available', type: 'error' });
      return;
    }

    setIsLeaveSubmitting(true);
    setNotification({ message: '', type: '' });

    try {
      const formData = {
        employerId: employee?.employer,
        employeeId: employee._id,
        ...Object.fromEntries(new FormData(e.target)),
      };

         if (formData.reason === 'Others' && formData.others) {
        formData.reason = formData.others;
        delete formData.others; 
      }
      console.log('Requesting leave with formData:', formData);

      const result = await dispatch(requestLeave(formData)).unwrap();
      setNotification({ message: result.message || 'Leave request submitted successfully', type: 'success' });
      toast.success(result.message || 'Leave request submitted successfully');
      e.target.reset();
    } catch (err) {
      console.error('Leave request error:', err);
      setNotification({ message: err.message || 'Failed to submit leave request', type: 'error' });
    } finally {
      setIsLeaveSubmitting(false);
    }
  };

  const calculateBreakdown = (deductions) => {
    return deductions || { tax: 0, penalty: 0, hmo: 0, other: 0 };
  };

  const generatePayslipData = (history = null) => {
    if (!employee?.salary) {
      return { employee: 'N/A', salary: { grossSalary: 0, deductions: {}, netSalary: 0 } };
    }
    if (history) {
      return {
        employee: `${employee.firstName} ${employee.lastName}`,
        salary: {
          grossSalary: history.grossSalary || 0,
          deductions: history.deductions || { tax: 0, penalty: 0, hmo: 0, other: 0 },
          netSalary: history.netSalary || 0,
        },
      };
    }
    const { gross, deductions, net, history: latestHistory } = employee.salary;
    const latest = latestHistory?.length ? latestHistory[latestHistory.length - 1] : {};
    return {
      employee: `${employee.firstName} ${employee.lastName}`,
      salary: {
        grossSalary: gross || latest.grossSalary || 0,
        deductions: deductions || latest.deductions || { tax: 0, penalty: 0, hmo: 0, other: 0 },
        netSalary: net || latest.netSalary || 0,
      },
    };
  };

  const payslipData = selectedHistory ? generatePayslipData(selectedHistory) : generatePayslipData();

  // Clear notification after 5 seconds
  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  console.log('Render - Redux employees:', employees);
  console.log('Render - Local employee:', employee);

  if (status === 'loading' && !employee) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
      </div>
    );
  }
  if (error && !employee) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        {typeof error === 'string' ? error : 'An error occurred while loading data'}
      </div>
    );
  }

   // Mock attendance records for now (replace with actual data from Redux or API if available)
  const mockAttendanceRecords = new Map();
  if (employee?._id) {
    mockAttendanceRecords.set(employee._id, new Map([
      ['2025-07-28', { status: 'present', timestamp: new Date('2025-07-28T10:00:00') }],
      ['2025-07-27', { status: 'absent', timestamp: new Date('2025-07-27T23:59:59') }],
      ['2025-06-15', { status: 'present', timestamp: new Date('2025-06-15T09:00:00') }],
    ]));
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Salaries and Leave</h2>

        {/* Notification Toast */}
        {notification.message && (
          <div
            className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg text-white ${
              notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            }`}
          >
            {notification.message}
          </div>
        )}

        {employee ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-semibold text-gray-700 mb-4">Salary Details</h3>
            <div className="overflow-x-auto">
              <table className="w-full bg-gray-50 border border-gray-200">
                <thead className="bg-orange-400 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left">Period</th>
                    <th className="py-3 px-4 text-left">Gross (N)</th>
                    <th className="py-3 px-4 text-left">Deductions (N)</th>
                    <th className="py-3 px-4 text-left">Net (N)</th>
                    <th className="py-3 px-4 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employee.salary?.history?.length > 0 ? (
                    employee.salary.history.map((hist, i) => (
                      <tr key={i} className="border-t hover:bg-gray-100">
                        <td className="py-3 px-4">{hist.month} {hist.year}</td>
                        <td className="py-3 px-4">{(hist.grossSalary || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          {Object.values(hist.deductions || {})
                            .reduce((a, b) => a + b, 0)
                            .toLocaleString() || 0}
                        </td>
                        <td className="py-3 px-4">{(hist.netSalary || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              setSelectedHistory(hist);
                              setIsPayslipModalOpen(true);
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
                          >
                            View Payslip
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
                        No salary history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-4">Apply for Leave</h3>
            <form onSubmit={handleRequestLeave} className="bg-gray-50 p-4 rounded-lg shadow-inner">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  name="startDate"
                  type="date"
                  required
                  className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  name="endDate"
                  type="date"
                  required
                  className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
  <select
      name="reason"
      required
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      className="p-2 border rounded-lg col-span-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="" disabled>
        Select Reason
      </option>
      <option value="Medicals">Medicals</option>
      <option value="Burial">Burial</option>
      <option value="Wedding">Wedding</option>
      <option value="Studies">Studies</option>
      <option value="Others">Others</option>
    </select>
    {reason === 'Others' && (
      <input
        name="others"
        type="text"
        placeholder="Please specify"
        required
        className="p-2 border rounded-lg col-span-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    )}
              </div>
              <button
                type="submit"
                disabled={isLeaveSubmitting}
                className={`w-full flex items-center justify-center px-4 py-2 rounded-lg text-white transition duration-200 ${
                  isLeaveSubmitting ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-700'
                }`}
              >
                {isLeaveSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Request Leave'
                )}
              </button>
            </form>

            <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-4">Leave Requests</h3>
            <div className="space-y-4">
              {employee.leaveRequests?.length > 0 ? (
                employee.leaveRequests.map((leave) => (
                  <div
                    key={leave._id}
                    className="bg-gray-50 p-4 rounded-lg shadow hover:shadow-md transition duration-200"
                  >
                    <p className="text-gray-700">
                      {leave.reason} ({new Date(leave.startDate).toLocaleDateString()} to{' '}
                      {new Date(leave.endDate).toLocaleDateString()}) - Date Requested:{' '}
                      {new Date(leave.createdAt).toLocaleDateString()} -{' '}
                      <span
                        className={
                          leave.status === 'Approved'
                            ? 'text-green-600 font-medium'
                            : leave.status === 'Rejected'
                            ? 'text-red-600 font-medium'
                            : 'text-yellow-600 font-medium'
                        }
                      >
                        status: {leave.status}
                      </span>
                    </p>
                    {leave.response && <p className="text-gray-600 mt-1">Response: {leave.response}</p>}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center">No leave requests available</p>
              )}
            </div>

          </div>
        ) : (
          <p className="text-gray-500 text-center">No employee data available. Please contact your employer.</p>
        )}

        {isPayslipModalOpen && payslipData.employee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Payslip</h3>
                <button
                  onClick={() => {
                    setIsPayslipModalOpen(false);
                    setSelectedHistory(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <BiX size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <p>
                  <strong>Employee:</strong> {payslipData.employee}
                </p>
                <p>
                  <strong>Gross Salary:</strong> N{(payslipData.salary.grossSalary || 0).toLocaleString()}
                </p>
                <p>
                  <strong>Deductions:</strong> N{
                    Object.values(payslipData.salary.deductions || {})
                      .reduce((a, b) => a + b, 0)
                      .toLocaleString() || 0
                  }
                </p>
                <p>
                  <strong>Net Salary:</strong> N{(payslipData.salary.netSalary || 0).toLocaleString()}
                </p>
                <div>
                  <strong>Breakdown:</strong>
                  <table className="w-full mt-2">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-1 px-2">Type</th>
                        <th className="py-1 px-2">Amount (N)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(calculateBreakdown(payslipData.salary.deductions)).map(([type, amount]) => (
                        <tr key={type} className="border-t">
                          <td className="py-1 px-2 capitalize">{type}</td>
                          <td className="py-1 px-2">{(amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    const element = document.createElement('a');
                    const file = new Blob(
                      [
                        `Payslip for ${payslipData.employee}\nGross: N${(payslipData.salary.grossSalary || 0).toLocaleString()}\nDeductions: N${Object.values(payslipData.salary.deductions || {})
                          .reduce((a, b) => a + b, 0)
                          .toLocaleString()}\nNet: N${(payslipData.salary.netSalary || 0).toLocaleString()}`
                      ],
                      { type: 'text/plain' }
                    );
                    element.href = URL.createObjectURL(file);
                    element.download = `payslip_${payslipData.employee.replace(/ /g, '_')}_${selectedHistory?.month || 'latest'}_${selectedHistory?.year || new Date().getFullYear()}.txt`;
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 w-full"
                >
                  <BiDownload /> Download Payslip
                </button>
              </div>
            </div>
          </div>
        )}
                <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-4">Attendance</h3>
            <EmployeeAttendance employeeId={employee?._id} attendanceRecords={mockAttendanceRecords} />
      </div>
    </div>
  );
};

export default EmployerEmployeeJobSeeker;


















































































